import { ReactNode, useCallback, useMemo } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { UseInfiniteQueryResultType } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { ComboboxAvatarItem, ComboboxAvatarItemOption } from '@sib/ui';

interface UseAutocompletePropsOptions<
  TItem,
  TOption extends ComboboxAvatarItemOption = ComboboxAvatarItemOption,
> {
  params: UseInfiniteQueryResultType<{ items: TItem[] }>;
  filter: string;
  setFilter?: (value: string) => void;
  optionsMapper: (item: TItem) => TOption | null;
  additionalFields?: TOption[];
  OptionComponent?: (option: TOption) => ReactNode;
}

export const useAutocompleteProps = <
  TItem,
  TOption extends ComboboxAvatarItemOption = ComboboxAvatarItemOption,
>({
  params,
  filter,
  setFilter,
  optionsMapper,
  additionalFields = [],
  OptionComponent = ComboboxAvatarItem,
}: UseAutocompletePropsOptions<TItem, TOption>) => {
  const { t } = useTranslation();
  const {
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    data,
    isLoading,
  } = params;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: () => !isFetchingNextPage && fetchNextPage(),
    disabled: !!error,
    rootMargin: '0px 0px 0px 0px',
  });

  const dataItems = useMemo(
    () =>
      data?.pages
        ? (data.pages || [])
            .map(item => item.items)
            .flat()
            .map(optionsMapper)
            .filter((item): item is TOption => item !== null)
            .filter(
              item => !additionalFields?.map(i => i.value).includes(item.value)
            )
        : [],
    [data, optionsMapper, additionalFields]
  );

  const onInputValueChange = useCallback(
    (value: string) => setFilter?.(value),
    [setFilter]
  );

  const options = useMemo(
    () => [...additionalFields, ...dataItems],
    [dataItems, additionalFields]
  );

  return useMemo(
    () => ({
      disabled: isLoading,
      isLoading: isLoading || isFetchingNextPage,
      inputValue: filter,
      options,
      hasNextPage,
      sentryRef,
      onInputValueChange,
      OptionComponent,
      emptyMessage: t('components.autocomplete.notSelected'),
      noResultMessage: t('noResults'),
      searchPlaceholder: t('components.autocomplete.searchPlaceholder'),
      clearText: t('clear'),
    }),
    [
      filter,
      isLoading,
      isFetchingNextPage,
      options,
      hasNextPage,
      sentryRef,
      onInputValueChange,
      OptionComponent,
      t,
    ]
  );
};
